import {
  normalizeDialablePhoneNumber,
  type LastDialDto,
  type OutboundCallPreparationDto,
} from '@pstn-twilio/shared';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';

import { AiAgentPanel } from '../components/ai-agent-panel';
import { CallQualityPanel } from '../components/call-quality';
import { EmailFinderStatus } from '../components/email-finder-status';
import { FbGroupsButton } from '../components/fb-groups-button';
import { MetaAdFilterButton } from '../components/meta-ad-filter-button';
import { MicrophonePicker } from '../components/microphone-picker';
import { PersonalizeButton } from '../components/personalize-button';
import { postCallDraftKey, PostCallStatusPanel } from '../components/post-call-status-panel';
import { SocialEnrichmentButton } from '../components/social-enrichment-button';
import { readPersistedSpreadsheet, SpreadsheetPicker } from '../components/spreadsheet-picker';
import { VoiceRecovery } from '../components/voice-recovery';
import { useVoiceDevice } from '../hooks/use-voice-device';
import { api, ApiError } from '../lib/api-client';
import {
  describeBrowserRecording,
  isBrowserRecordingSupported,
  isUploadSkipped,
  recordCallInBrowser,
  setUploadSkipped,
  useBrowserRecordingStatus,
} from '../lib/browser-recordings';
import type { RecordableCall } from '../lib/call-recorder';
import { formatDate, formatPhone } from '../lib/format';
import { watchRecordingDownload } from '../lib/recording-downloads';

const DIALPAD_KEYS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '*', '0', '#', '+'] as const;
const RECORD_CALLS_STORAGE_KEY = 'pstn-twilio.record-calls';
// Whether Twilio also records calls the browser records. On while browser
// recordings are being compared against Twilio's; turn it off once they hold up.
const TWILIO_BACKUP_STORAGE_KEY = 'pstn-twilio.twilio-backup';

// Calls were always recorded before these settings existed, so recording stays
// on until the user turns it off.
function readBooleanPreference(key: string): boolean {
  try {
    return window.localStorage.getItem(key) !== 'false';
  } catch {
    return true;
  }
}

function writeBooleanPreference(key: string, value: boolean): void {
  try {
    window.localStorage.setItem(key, String(value));
  } catch {
    // Not persisted; the toggle still applies to this page.
  }
}

// One status panel per outbound call, from the moment it starts until its
// statuses are pushed or it is dismissed. Kept in the browser so a reload (Chrome
// on Android discards background tabs) does not lose them.
const CALL_PANELS_STORAGE_KEY = 'pstn-twilio.post-call.panels';

interface CallPanel {
  id: string;
  destinationE164: string;
  callerE164: string;
  /** null while the call is up. */
  endedAt: string | null;
}

function readPersistedCallPanels(): CallPanel[] {
  try {
    const panels = JSON.parse(
      window.localStorage.getItem(CALL_PANELS_STORAGE_KEY) ?? '[]',
    ) as CallPanel[];
    // A reload ends any call that was up, so it has ended by now.
    const now = new Date().toISOString();
    return panels.map((p) => ({ ...p, endedAt: p.endedAt ?? now }));
  } catch {
    return [];
  }
}

function writePersistedCallPanels(panels: CallPanel[]): void {
  try {
    window.localStorage.setItem(CALL_PANELS_STORAGE_KEY, JSON.stringify(panels));
    const live = new Set(panels.map((p) => postCallDraftKey(p.id)));
    const stale: string[] = [];
    for (let i = 0; i < window.localStorage.length; i++) {
      const key = window.localStorage.key(i);
      if (key && key.startsWith(postCallDraftKey('')) && !live.has(key)) stale.push(key);
    }
    for (const key of stale) window.localStorage.removeItem(key);
  } catch {
    // Not persisted; the panels still work on this page.
  }
}

function isMissingLastDialEndpointError(err: unknown): boolean {
  return err instanceof ApiError && err.status === 404;
}

function StatusPill({ label, ok, pending }: { label: string; ok: boolean; pending?: boolean }) {
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
        ok
          ? 'bg-emerald-100 text-emerald-700'
          : pending
            ? 'bg-amber-100 text-amber-800'
            : 'bg-slate-100 text-slate-600'
      }`}
    >
      {label}
    </span>
  );
}

function formatDuration(seconds: number | null): string {
  if (seconds === null) return '—';
  const rounded = Math.round(seconds);
  if (rounded < 60) return `${rounded}s`;
  return `${Math.floor(rounded / 60)}m ${rounded % 60}s`;
}

export function DialPage() {
  const { numberId } = useParams<{ numberId: string }>();
  const [destination, setDestination] = useState('');
  const selectedNumber = useQuery({
    queryKey: ['numbers', numberId],
    queryFn: () => api.numbers.get(numberId!),
    enabled: Boolean(numberId),
  });
  const callerId = selectedNumber.data?.phoneNumberE164;
  const dialingCountry =
    selectedNumber.data?.country === 'GB' || callerId?.startsWith('+44') ? 'GB' : 'US';
  const isUk = dialingCountry === 'GB';
  const callInFlight = useRef(false);
  const clipboardInFlight = useRef(false);
  const [submitting, setSubmitting] = useState(false);
  const [pageError, setPageError] = useState<string | null>(null);
  const [sentTones, setSentTones] = useState('');
  const [repeatDialWarning, setRepeatDialWarning] = useState<LastDialDto | null>(null);
  const [recordCall, setRecordCall] = useState<boolean>(() =>
    readBooleanPreference(RECORD_CALLS_STORAGE_KEY),
  );
  const [twilioBackup, setTwilioBackup] = useState<boolean>(() =>
    readBooleanPreference(TWILIO_BACKUP_STORAGE_KEY),
  );
  const [browserRecordingSupported] = useState(isBrowserRecordingSupported);
  const [activeCallRecorded, setActiveCallRecorded] = useState(false);
  const [activeCallBrowserRecorded, setActiveCallBrowserRecorded] = useState(false);
  const [activeCallTwilioRecorded, setActiveCallTwilioRecorded] = useState(false);
  const [backupBusy, setBackupBusy] = useState(false);
  const [backupError, setBackupError] = useState<string | null>(null);
  // "No R2": the call that is up, or between calls the next one, has its
  // recording deleted when it ends instead of uploaded. It switches back off
  // when the call ends, so a forgotten switch cannot delete a real call.
  const [noR2, setNoR2] = useState(isUploadSkipped);
  const noR2Ref = useRef(noR2);
  const activeCallRef = useRef<RecordableCall | null>(null);
  const latestRecording = useBrowserRecordingStatus();
  const browserRecording = latestRecording?.direction === 'outbound' ? latestRecording : null;

  // Google Sheets target for post-call status pushes, remembered across visits.
  const [spreadsheetId, setSpreadsheetId] = useState<string | null>(
    () => readPersistedSpreadsheet().spreadsheetId,
  );
  const [sheetTitle, setSheetTitle] = useState<string | null>(
    () => readPersistedSpreadsheet().sheetTitle,
  );
  // The outbound call in progress, and the status panels (see CallPanel).
  const outboundCallRef = useRef<Omit<CallPanel, 'endedAt'> | null>(null);
  const [callPanels, setCallPanels] = useState<CallPanel[]>(readPersistedCallPanels);
  const unsavedPanels = useRef(new Set<string>());
  const trackUnsaved = useCallback((id: string, unsaved: boolean) => {
    if (unsaved) unsavedPanels.current.add(id);
    else unsavedPanels.current.delete(id);
  }, []);
  useEffect(() => writePersistedCallPanels(callPanels), [callPanels]);

  const voice = useVoiceDevice();
  const outboundAnalytics = useQuery({
    queryKey: ['calls', numberId, 'analytics', 30],
    queryFn: () => api.calls.outboundAnalytics(numberId!, 30),
    enabled: Boolean(numberId),
    staleTime: 60_000,
    refetchInterval: 60_000,
    refetchOnWindowFocus: false,
  });
  const inCallMode =
    voice.active ||
    voice.canSendDigits ||
    voice.connectionState === 'pending' ||
    voice.connectionState === 'ringing' ||
    voice.connectionState === 'open';
  const sendDtmfDigits = voice.sendDigits;

  function setDestinationFromInput(value: string) {
    // Keep partial UK numbers intact: a valid ten-digit prefix can still be
    // part of an eleven-digit national number while the user is typing.
    setDestination(isUk ? value.trim() : (normalizeDialablePhoneNumber(value) ?? value.trim()));
  }

  function appendDestinationKey(key: (typeof DIALPAD_KEYS)[number]) {
    if (key === '+') {
      setDestination((prev) => {
        const trimmed = prev.trim();
        return trimmed.startsWith('+') ? trimmed : `+${trimmed}`;
      });
      return;
    }

    setDestination((prev) =>
      isUk ? prev + key : (normalizeDialablePhoneNumber(prev + key) ?? prev + key),
    );
  }

  const sendDialpadTone = useCallback(
    (key: string) => {
      sendDtmfDigits(key);
      setSentTones((prev) => `${prev}${key}`.slice(-24));
    },
    [sendDtmfDigits],
  );

  function handleDialpadKey(key: (typeof DIALPAD_KEYS)[number]) {
    if (inCallMode) {
      if (key !== '+') {
        sendDialpadTone(key);
        setDestination((prev) => prev + key);
      }
      return;
    }

    appendDestinationKey(key);
  }

  useEffect(() => {
    if (inCallMode) {
      // An incoming call answered while "No R2" is on stays out of R2 too.
      if (noR2Ref.current) setUploadSkipped(true);
      return;
    }
    setSentTones('');
    setActiveCallRecorded(false);
    setActiveCallBrowserRecorded(false);
    setActiveCallTwilioRecorded(false);
    setBackupError(null);
    activeCallRef.current = null;
    // Only the switch: the call that just ended keeps its own setting.
    noR2Ref.current = false;
    setNoR2(false);
  }, [inCallMode]);

  // An outbound call opens its status panel as soon as it starts, so notes can
  // be taken during the call; the same panel gets the hangup time when it ends.
  // Earlier panels stay only while they hold work that has not been pushed.
  const wasInCallRef = useRef(false);
  useEffect(() => {
    if (inCallMode) {
      wasInCallRef.current = true;
      const call = outboundCallRef.current;
      if (call) {
        setCallPanels((prev) =>
          prev.some((p) => p.id === call.id)
            ? prev
            : [...prev.filter((p) => unsavedPanels.current.has(p.id)), { ...call, endedAt: null }],
        );
      }
      return;
    }
    if (!wasInCallRef.current) return;
    wasInCallRef.current = false;
    const call = outboundCallRef.current;
    outboundCallRef.current = null;
    if (call) {
      const endedAt = new Date().toISOString();
      setCallPanels((prev) => prev.map((p) => (p.id === call.id ? { ...p, endedAt } : p)));
    }
  }, [inCallMode]);

  function toggleRecordCall() {
    setRecordCall((prev) => {
      writeBooleanPreference(RECORD_CALLS_STORAGE_KEY, !prev);
      return !prev;
    });
  }

  function toggleTwilioBackup() {
    setTwilioBackup((prev) => {
      writeBooleanPreference(TWILIO_BACKUP_STORAGE_KEY, !prev);
      return !prev;
    });
  }

  function toggleNoR2() {
    const next = !noR2Ref.current;
    noR2Ref.current = next;
    setNoR2(next);
    setUploadSkipped(next);
  }

  // Has Twilio record the rest of the live call, e.g. once it is going well.
  async function backUpActiveCall() {
    const callSid = activeCallRef.current?.parameters?.CallSid;
    if (!callSid) {
      setBackupError('The call is still connecting. Try again in a moment.');
      return;
    }
    setBackupBusy(true);
    setBackupError(null);
    try {
      await api.recordings.startTwilioBackup(callSid);
      setActiveCallTwilioRecorded(true);
      setActiveCallRecorded(true);
    } catch (err) {
      setBackupError(err instanceof Error ? err.message : String(err));
    } finally {
      setBackupBusy(false);
    }
  }

  useEffect(() => {
    if (!inCallMode) return;

    function handleKeyDown(event: KeyboardEvent) {
      if (event.altKey || event.ctrlKey || event.metaKey) return;
      if (!/^[0-9*#]$/.test(event.key)) return;
      event.preventDefault();
      sendDialpadTone(event.key);
      setDestination((prev) => prev + event.key);
    }

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [inCallMode, sendDialpadTone]);

  const normalizedDestination = useMemo(
    () => normalizeDialablePhoneNumber(destination, dialingCountry),
    [destination, dialingCountry],
  );
  const valid = normalizedDestination !== null;

  async function placeCall(
    destinationNumber: string | null = normalizedDestination,
    opts: { skipRepeatWarning?: boolean } = {},
  ) {
    if (
      callInFlight.current ||
      inCallMode ||
      !selectedNumber.data ||
      voice.reconnecting ||
      voice.recoveryFailed
    )
      return;
    setPageError(null);
    if (!numberId) return;
    if (!destinationNumber) {
      setPageError(
        isUk
          ? 'Enter a UK phone number, such as 020 7946 0018 or +44 20 7946 0018.'
          : 'Enter a valid U.S. phone number, such as +1 530-441-9961.',
      );
      return;
    }
    if (voice.micPermission === 'denied') {
      setPageError(
        'Microphone permission was denied (31401). Please allow microphone permissions in your browser settings (click the lock or tune icon next to the address bar) and try again.',
      );
      return;
    }
    callInFlight.current = true;
    setSubmitting(true);
    try {
      if (voice.micPermission !== 'granted' && typeof voice.requestMicPermission === 'function') {
        const granted = await voice.requestMicPermission();
        if (!granted) return;
      }
      setDestination(destinationNumber);
      if (!opts.skipRepeatWarning) {
        let lastDial: LastDialDto | null = null;
        try {
          lastDial = await api.calls.lastDial(numberId, destinationNumber);
        } catch (err) {
          if (!isMissingLastDialEndpointError(err)) throw err;
        }
        if (lastDial) {
          setRepeatDialWarning(lastDial);
          return;
        }
      }
      const prepared: { current: OutboundCallPreparationDto | null } = { current: null };
      outboundCallRef.current = {
        id: `call-${Date.now().toString(36)}`,
        destinationE164: destinationNumber,
        callerE164: selectedNumber.data.phoneNumberE164,
      };
      // The browser records for free; Twilio records too as a backup, or
      // instead when this browser cannot record. Not with "No R2" on: every
      // Twilio recording is archived to R2.
      const recordInBrowser = recordCall && browserRecordingSupported;
      const recordOnTwilio =
        recordCall && !noR2Ref.current && (twilioBackup || !browserRecordingSupported);
      const call = await voice.makeCall(numberId, destinationNumber, {
        recordCall: recordOnTwilio,
        onPrepared: (p) => {
          prepared.current = p;
        },
      });
      if (!call) outboundCallRef.current = null;
      activeCallRef.current = call;
      if (call && prepared.current) {
        const intent = prepared.current;
        const browserRecorded =
          recordInBrowser &&
          recordCallInBrowser(call, {
            direction: 'outbound',
            counterpart: intent.destinationNumber,
            numberId,
            outboundIntentId: intent.outboundIntentId,
          });
        // "No R2" switched on before this call's recording was set up.
        if (browserRecorded && noR2Ref.current) setUploadSkipped(true);
        // An API that predates the setting omits recordCall and always records.
        const twilioRecorded = intent.recordCall !== false;
        setActiveCallRecorded(browserRecorded || twilioRecorded);
        setActiveCallBrowserRecorded(browserRecorded);
        setActiveCallTwilioRecorded(twilioRecorded);
        // The browser saves its own file; otherwise download Twilio's MP3.
        if (twilioRecorded && !browserRecorded) {
          watchRecordingDownload({
            outboundIntentId: intent.outboundIntentId,
            numberId,
            destination: intent.destinationNumber,
            intentExpiresAt: intent.expiresAt,
          });
        }
      }
    } catch (err) {
      if (!wasInCallRef.current) outboundCallRef.current = null;
      setPageError(err instanceof Error ? err.message : String(err));
    } finally {
      callInFlight.current = false;
      setSubmitting(false);
    }
  }

  async function handleCall() {
    await placeCall();
  }

  async function handlePasteAndCall() {
    if (clipboardInFlight.current || callInFlight.current || inCallMode || !selectedNumber.data)
      return;
    setPageError(null);
    if (!navigator.clipboard?.readText) {
      setPageError('Clipboard access is unavailable in this browser.');
      return;
    }

    clipboardInFlight.current = true;
    setSubmitting(true);
    try {
      const pasted = await navigator.clipboard.readText();
      const normalized = normalizeDialablePhoneNumber(pasted, dialingCountry);
      if (!normalized) {
        setPageError(
          isUk
            ? 'Copy one complete UK phone number, or text containing just one phone number.'
            : 'Clipboard does not contain a dialable phone number.',
        );
        return;
      }
      setDestination(normalized);
      await placeCall(normalized, { skipRepeatWarning: isUk });
    } catch (err) {
      setPageError(err instanceof Error ? err.message : String(err));
    } finally {
      clipboardInFlight.current = false;
      setSubmitting(false);
    }
  }

  async function confirmRepeatDial() {
    if (!repeatDialWarning) return;
    const destinationNumber = repeatDialWarning.destinationNumber;
    setRepeatDialWarning(null);
    await placeCall(destinationNumber, { skipRepeatWarning: true });
  }

  function cancelRepeatDial() {
    setRepeatDialWarning(null);
  }

  function handleHangup() {
    voice.hangup();
  }

  return (
    <section className="space-y-4">
      <header>
        <h1 className="text-2xl font-semibold">Dial · {numberId}</h1>
        <p className="mt-1 text-sm text-slate-600">
          Place a PSTN call from your selected Twilio number. Caller ID is locked to this number and
          cannot be spoofed.
        </p>
      </header>

      <div className="rounded border border-slate-200 bg-white p-4">
        <h2 className="text-sm font-semibold text-slate-700">Device readiness</h2>
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <StatusPill
            label={voice.browserSupported ? 'WebRTC supported' : 'WebRTC unavailable'}
            ok={voice.browserSupported}
          />
          <StatusPill
            label={
              voice.registered
                ? 'Registered'
                : voice.reconnecting
                  ? 'Reconnecting…'
                  : 'Not registered'
            }
            ok={voice.registered}
            pending={voice.reconnecting}
          />
          {!voice.reconnecting && (
            <StatusPill
              label={
                voice.recoveryFailed
                  ? 'Connection unavailable'
                  : voice.ready
                    ? 'Ready'
                    : 'Initializing…'
              }
              ok={voice.ready}
            />
          )}
          <StatusPill
            label={`Mic: ${voice.micPermission}`}
            ok={voice.micPermission === 'granted'}
          />
          {voice.active && voice.wakeLockSupported && (
            <StatusPill
              label={voice.wakeLockHeld ? 'Screen awake' : 'Keeping screen awake…'}
              ok={voice.wakeLockHeld}
              pending={!voice.wakeLockHeld}
            />
          )}
          {voice.micPermission !== 'granted' && voice.browserSupported && (
            <button
              type="button"
              onClick={() => void voice.requestMicPermission()}
              className="rounded border border-amber-300 bg-amber-50 px-2.5 py-0.5 text-xs font-medium text-amber-900 hover:bg-amber-100"
            >
              {voice.micPermission === 'denied'
                ? 'Re-check Microphone Permission'
                : 'Enable Microphone'}
            </button>
          )}
        </div>
        {callerId && (
          <p className="mt-2 text-xs text-slate-500">
            Caller ID: <span className="font-mono">{callerId}</span>
          </p>
        )}
      </div>

      <MicrophonePicker voice={voice} />
      <VoiceRecovery voice={voice} />

      <div className="rounded border border-slate-200 bg-white p-4">
        <div className="flex items-baseline justify-between gap-3">
          <h2 className="text-sm font-semibold text-slate-700">Outbound performance</h2>
          <span className="text-xs text-slate-500">Last 30 days</span>
        </div>
        {outboundAnalytics.isLoading && <p className="mt-2 text-sm text-slate-500">Loading…</p>}
        {outboundAnalytics.isError && (
          <p className="mt-2 text-sm text-amber-800">
            Outbound performance is unavailable right now.
          </p>
        )}
        {outboundAnalytics.data && (
          <div className="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
            <div className="rounded border border-slate-100 bg-slate-50 p-2.5">
              <p className="text-xs text-slate-500">Calls placed</p>
              <p className="mt-0.5 text-lg font-semibold text-slate-900">
                {outboundAnalytics.data.totalCalls}
              </p>
            </div>
            <div className="rounded border border-slate-100 bg-slate-50 p-2.5">
              <p className="text-xs text-slate-500">Answer rate</p>
              <p className="mt-0.5 text-lg font-semibold text-slate-900">
                {outboundAnalytics.data.answerRatePercent === null
                  ? '—'
                  : `${outboundAnalytics.data.answerRatePercent}%`}
              </p>
              <p className="text-xs text-slate-500">
                {outboundAnalytics.data.answeredCalls} answered
              </p>
            </div>
            <div className="rounded border border-slate-100 bg-slate-50 p-2.5">
              <p className="text-xs text-slate-500">Average duration</p>
              <p className="mt-0.5 text-lg font-semibold text-slate-900">
                {formatDuration(outboundAnalytics.data.averageDurationSeconds)}
              </p>
            </div>
            <div className="rounded border border-slate-100 bg-slate-50 p-2.5">
              <p className="text-xs text-slate-500">Unsuccessful</p>
              <p className="mt-0.5 text-lg font-semibold text-slate-900">
                {outboundAnalytics.data.unsuccessfulCalls}
              </p>
              <p className="text-xs text-slate-500">
                {outboundAnalytics.data.statusCounts.NO_ANSWER} no answer ·{' '}
                {outboundAnalytics.data.statusCounts.BUSY} busy
              </p>
            </div>
          </div>
        )}
      </div>

      {!voice.browserSupported && (
        <div className="rounded border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800">
          Your browser does not support WebRTC. Use the latest Chrome, Edge, or Firefox.
        </div>
      )}
      {voice.micPermission === 'denied' && (
        <div className="rounded border border-rose-300 bg-rose-50 p-3 text-sm text-rose-800">
          <p className="font-semibold">Microphone access is blocked (Twilio 31401)</p>
          <p className="mt-1 text-xs">
            The browser or user denied permissions to the microphone. To fix this:
          </p>
          <ol className="mt-1 list-decimal list-inside space-y-0.5 text-xs">
            <li>Click the lock or site settings icon in your browser address bar.</li>
            <li>
              Change the <strong>Microphone</strong> permission from Block to <strong>Allow</strong>
              .
            </li>
            <li>
              Click <strong>Re-check Microphone Permission</strong> above or refresh the page.
            </li>
          </ol>
        </div>
      )}
      {(pageError || voice.error) && voice.micPermission !== 'denied' && (
        <div className="rounded border border-rose-300 bg-rose-50 p-3 text-sm text-rose-800">
          {pageError ?? voice.error}
        </div>
      )}
      {selectedNumber.isError && (
        <p role="alert">Could not load this phone number. Refresh before dialing.</p>
      )}
      {voice.callNotice && (
        <p
          role="status"
          className="rounded border border-slate-200 bg-white p-3 text-sm text-slate-700"
        >
          {voice.callNotice}
        </p>
      )}

      <div className="rounded border border-slate-200 bg-white p-4">
        <div className="flex items-end justify-between gap-2">
          <label htmlFor="destination-number" className="text-sm text-slate-700">
            Destination (E.164)
          </label>
          {/* Never locked: a call often turns out to be a voicemail only once it is up. */}
          <button
            type="button"
            aria-pressed={noR2}
            onClick={toggleNoR2}
            title={
              noR2
                ? "On: this call's recording is deleted when it ends instead of going to R2"
                : "Keep this call's recording out of R2"
            }
            className={`rounded border px-3 py-1.5 text-sm font-medium transition-colors ${
              noR2
                ? 'border-amber-600 bg-amber-400 text-amber-950 hover:bg-amber-300'
                : 'border-slate-300 hover:bg-slate-50'
            }`}
          >
            No R2
          </button>
        </div>
        <div className="mt-1 flex gap-2">
          <input
            id="destination-number"
            value={destination}
            onChange={(e) => setDestinationFromInput(e.target.value)}
            onPaste={(e) => {
              e.preventDefault();
              if (inCallMode || submitting || !selectedNumber.data) return;
              const normalized = normalizeDialablePhoneNumber(
                e.clipboardData.getData('text'),
                dialingCountry,
              );
              if (!normalized) {
                setPageError('Pasted text does not contain a dialable phone number.');
                return;
              }
              setPageError(null);
              setDestination(normalized);
              if (isUk) void placeCall(normalized, { skipRepeatWarning: true });
            }}
            placeholder={isUk ? '020 7946 0018' : '+1 530-441-9961'}
            inputMode="tel"
            readOnly={inCallMode}
            className="min-w-0 flex-1 rounded border border-slate-300 px-2 py-1 font-mono text-sm focus:border-slate-500 focus:outline-none read-only:bg-slate-50"
          />
          <button
            type="button"
            onClick={handlePasteAndCall}
            disabled={
              !selectedNumber.data ||
              submitting ||
              inCallMode ||
              voice.micPermission === 'denied' ||
              voice.reconnecting ||
              voice.recoveryFailed
            }
            title="Paste a phone number from the clipboard and call it"
            className="rounded border border-slate-300 px-3 py-1.5 text-sm font-medium hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
          >
            Paste
          </button>
        </div>
        {isUk && (
          <p className="mt-2 text-xs text-slate-500">
            UK dialing: paste a number to convert it to +44 and call immediately.
          </p>
        )}
        {destination.length > 0 && !valid && (
          <p className="mt-1 text-xs text-rose-700">
            {isUk
              ? 'Enter a UK number such as 020 7946 0018 or 0161 496 0123.'
              : 'Enter a U.S. phone number such as +1 530-441-9961 or 530-441-9961.'}
          </p>
        )}

        <div className="mt-3 grid grid-cols-3 gap-2">
          {DIALPAD_KEYS.map((d) => (
            <button
              key={d}
              type="button"
              onClick={() => handleDialpadKey(d)}
              disabled={inCallMode && d === '+'}
              className={`rounded border border-slate-200 px-3 py-2 text-base hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50 ${
                d === '+' ? 'col-start-2' : ''
              }`}
            >
              {d}
            </button>
          ))}
        </div>
        {sentTones && (
          <p className="mt-2 text-xs text-slate-500">
            Tones: <span className="font-mono">{sentTones}</span>
          </p>
        )}

        <div className="mt-4 rounded border border-slate-200 px-3 py-2">
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <p id="record-call-label" className="text-sm font-medium text-slate-800">
                Record call
                {inCallMode && activeCallRecorded && (
                  <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-rose-100 px-2 py-0.5 text-xs font-medium text-rose-700">
                    <span
                      className="h-1.5 w-1.5 animate-pulse rounded-full bg-rose-600"
                      aria-hidden="true"
                    />
                    Recording
                  </span>
                )}
              </p>
              <p id="record-call-hint" className="text-xs text-slate-500">
                {inCallMode
                  ? activeCallRecorded
                    ? activeCallBrowserRecorded
                      ? noR2
                        ? 'Recording in this browser. No R2 is on, so the recording is deleted when the call ends.'
                        : 'Recording in this browser. If the call reaches 90 seconds, the file downloads when it ends.'
                      : noR2
                        ? 'Twilio is recording this call. No R2 does not stop its copy going to R2.'
                        : 'The MP3 downloads automatically after the call ends.'
                    : 'Changes apply to your next call.'
                  : !recordCall
                    ? 'The call will not be recorded.'
                    : noR2
                      ? browserRecordingSupported
                        ? "No R2 is on: the next call's recording is deleted when it ends."
                        : 'No R2 is on: the next call is not recorded.'
                      : browserRecordingSupported
                        ? 'Recorded in this browser for free. Calls of 90 seconds or more download when they end and are added to the call log; shorter ones are deleted.'
                        : 'The call is recorded and the MP3 downloads automatically when it ends.'}
              </p>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={recordCall}
              aria-labelledby="record-call-label"
              aria-describedby="record-call-hint"
              onClick={toggleRecordCall}
              disabled={inCallMode || submitting}
              className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60 ${
                recordCall ? 'bg-rose-600' : 'bg-slate-300'
              }`}
            >
              <span
                aria-hidden="true"
                className={`inline-block h-5 w-5 rounded-full bg-white shadow transition-transform ${
                  recordCall ? 'translate-x-5' : 'translate-x-0.5'
                }`}
              />
            </button>
          </div>

          {recordCall && browserRecordingSupported && (
            <div className="mt-2 flex items-center justify-between gap-3 border-t border-slate-100 pt-2">
              <div className="min-w-0">
                <p id="twilio-backup-label" className="text-sm text-slate-800">
                  Twilio backup copy
                </p>
                <p id="twilio-backup-hint" className="text-xs text-slate-500">
                  {inCallMode
                    ? activeCallTwilioRecorded
                      ? noR2
                        ? 'Twilio is also recording this call. No R2 does not stop its copy going to R2.'
                        : 'Twilio is also recording this call.'
                      : noR2
                        ? 'Turn off No R2 to back this call up on Twilio.'
                        : 'Have Twilio record the rest of this call ($0.0025/min).'
                    : twilioBackup
                      ? noR2
                        ? 'Not for the next call: No R2 is on.'
                        : 'Twilio also records each call ($0.0025/min) while browser recordings prove reliable.'
                      : 'Off. During a call you can still back it up on Twilio.'}
                </p>
              </div>
              {inCallMode ? (
                activeCallTwilioRecorded ? (
                  <span className="shrink-0 rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-700">
                    On
                  </span>
                ) : (
                  <button
                    type="button"
                    onClick={() => void backUpActiveCall()}
                    disabled={backupBusy || voice.connectionState !== 'open' || noR2}
                    className="shrink-0 rounded border border-slate-300 px-2 py-1 text-xs font-medium hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {backupBusy ? 'Starting…' : 'Back up this call'}
                  </button>
                )
              ) : (
                <button
                  type="button"
                  role="switch"
                  aria-checked={twilioBackup}
                  aria-labelledby="twilio-backup-label"
                  aria-describedby="twilio-backup-hint"
                  onClick={toggleTwilioBackup}
                  disabled={submitting}
                  className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60 ${
                    twilioBackup ? 'bg-slate-700' : 'bg-slate-300'
                  }`}
                >
                  <span
                    aria-hidden="true"
                    className={`inline-block h-4 w-4 rounded-full bg-white shadow transition-transform ${
                      twilioBackup ? 'translate-x-4' : 'translate-x-0.5'
                    }`}
                  />
                </button>
              )}
            </div>
          )}
          {backupError && <p className="mt-1 text-xs text-rose-700">{backupError}</p>}
          {browserRecording && (
            <p
              role="status"
              className={`mt-2 break-words text-xs ${
                browserRecording.state === 'failed'
                  ? 'text-rose-700'
                  : browserRecording.state === 'waiting'
                    ? 'text-amber-700'
                    : 'text-slate-600'
              }`}
            >
              {describeBrowserRecording(browserRecording)}
            </p>
          )}
        </div>

        <div className="mt-4 flex flex-wrap gap-2">
          <button
            onClick={handleCall}
            disabled={
              !selectedNumber.data ||
              voice.reconnecting ||
              voice.recoveryFailed ||
              !valid ||
              submitting ||
              inCallMode ||
              voice.micPermission === 'denied'
            }
            className="rounded bg-emerald-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {inCallMode ? 'In call' : submitting ? 'Calling…' : 'Call'}
          </button>
          <button
            onClick={() => voice.toggleMute()}
            disabled={!inCallMode}
            className="rounded border border-slate-300 px-3 py-1.5 text-sm disabled:opacity-60"
          >
            {voice.isMuted ? 'Unmute' : 'Mute'}
          </button>
          <button
            onClick={handleHangup}
            disabled={!inCallMode}
            className="rounded bg-rose-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-rose-700 disabled:opacity-60"
          >
            Hangup
          </button>
          <button
            onClick={() => setDestination('')}
            disabled={destination.length === 0 || inCallMode}
            className="ml-auto rounded border border-slate-300 px-3 py-1.5 text-sm disabled:opacity-60"
          >
            Clear
          </button>
        </div>

        <p className="mt-3 text-xs text-slate-500">
          Live state: <span className="font-mono">{voice.connectionState}</span>
        </p>
        {inCallMode && (
          <CallQualityPanel quality={voice.callQuality} warnings={voice.qualityWarnings} />
        )}
      </div>

      {[...callPanels].reverse().map((panel) => (
        <PostCallStatusPanel
          key={panel.id}
          callId={panel.id}
          destinationE164={panel.destinationE164}
          callerE164={panel.callerE164}
          callEndedAt={panel.endedAt}
          spreadsheetId={spreadsheetId}
          sheetTitle={sheetTitle}
          onUnsavedChange={trackUnsaved}
          onDismiss={() => {
            unsavedPanels.current.delete(panel.id);
            setCallPanels((prev) => prev.filter((p) => p.id !== panel.id));
          }}
        />
      ))}

      <div className="space-y-2 rounded border border-slate-200 bg-white p-4">
        <h2 className="text-sm font-semibold text-slate-700">Sheet target</h2>
        <p className="text-xs text-slate-500">
          The spreadsheet and tab you are calling from. After each call, the status goes to the row
          whose phoneNumber matches the number you dialled.
        </p>
        <SpreadsheetPicker
          spreadsheetId={spreadsheetId}
          sheetTitle={sheetTitle}
          onSpreadsheetChange={(id) => {
            setSpreadsheetId(id);
            setSheetTitle(null);
          }}
          onSheetTitleChange={setSheetTitle}
        />
        {spreadsheetId && sheetTitle && (
          <EmailFinderStatus
            key={`${spreadsheetId}:${sheetTitle}`}
            spreadsheetId={spreadsheetId}
            sheetTitle={sheetTitle}
          />
        )}
        {spreadsheetId && sheetTitle && (
          <SocialEnrichmentButton
            key={`enrich:${spreadsheetId}:${sheetTitle}`}
            spreadsheetId={spreadsheetId}
            sheetTitle={sheetTitle}
          />
        )}
        {spreadsheetId && sheetTitle && (
          <PersonalizeButton
            key={`personalize:${spreadsheetId}:${sheetTitle}`}
            spreadsheetId={spreadsheetId}
            sheetTitle={sheetTitle}
          />
        )}
        {spreadsheetId && sheetTitle && (
          <MetaAdFilterButton
            key={`meta-ads:${spreadsheetId}:${sheetTitle}`}
            spreadsheetId={spreadsheetId}
            sheetTitle={sheetTitle}
          />
        )}
        {/* Not tied to the selected tab: the searches come from both workbooks. */}
        <FbGroupsButton />
      </div>

      <AiAgentPanel destination={normalizedDestination} />

      {repeatDialWarning && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4">
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="repeat-dial-title"
            className="w-full max-w-md rounded border border-amber-300 bg-white p-4 shadow-lg"
          >
            <h2 id="repeat-dial-title" className="text-base font-semibold text-slate-900">
              Warning: number dialed before
            </h2>
            <p className="mt-2 text-sm text-slate-700">
              You last dialed{' '}
              <span className="font-mono">{formatPhone(repeatDialWarning.destinationNumber)}</span>{' '}
              on <span className="font-medium">{formatDate(repeatDialWarning.lastDialedAt)}</span>.
              Do you want to call this number again?
            </p>
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                onClick={cancelRepeatDial}
                disabled={submitting}
                className="rounded border border-slate-300 px-3 py-1.5 text-sm font-medium hover:bg-slate-50 disabled:opacity-60"
              >
                No
              </button>
              <button
                type="button"
                onClick={() => void confirmRepeatDial()}
                disabled={submitting}
                className="rounded bg-emerald-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-60"
              >
                Yes
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
